import { useState, useEffect } from 'react';
import { reportCenterService, propertyService } from 'services/api';
import PropertyCombobox from 'components/PropertyCombobox';
import { useAuth } from 'context/AuthContext';
import Paginacion from 'components/Paginacion';
import styles from './ImportReportes.module.css';

const PAGE_SIZE = 25;

const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function ImportReportes() {
  const { isSuperAdmin, user } = useAuth();
  const isAdminMultiProp = user?.role === 'admin';

  const [reportes,  setReportes]  = useState([]);
  const [page,      setPage]      = useState(1);
  const [retencion, setRetencion] = useState(30);
  const [loading,   setLoading]   = useState(true);
  const [alert,     setAlert]     = useState(null);

  // Selector de propiedad — mismo patrón que Asambleas/Unidades
  const [properties, setProperties] = useState([]);
  const [filterProp, setFilterProp] = useState('');

  useEffect(() => {
    if (isSuperAdmin || isAdminMultiProp) {
      propertyService.selector({ limit: 200 }).then(r => {
        const props = r.data.data || [];
        setProperties(props);
        if (isAdminMultiProp && !isSuperAdmin && props.length === 1) setFilterProp(String(props[0].id));
      });
    }
  }, [isSuperAdmin, isAdminMultiProp]);

  useEffect(() => {
    reportCenterService.getImportReports()
      .then(r => { setReportes(r.data.data || []); setRetencion(r.data.retencion_dias || 30); })
      .finally(() => setLoading(false));
  }, []);

  const visibleReportes = filterProp
    ? reportes.filter(r => String(r.property?.id) === String(filterProp))
    : reportes;

  // Paginación en el navegador (el servidor ya manda todo y solo guarda 30 días).
  const totalPages = Math.max(1, Math.ceil(visibleReportes.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = visibleReportes.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const cambiarPropiedad = (id) => { setFilterProp(id); setPage(1); };
  const irAPagina = (n) => { setPage(n); window.scrollTo({ top: 0 }); };

  const handleDownload = async (reporte) => {
    try {
      const r = await reportCenterService.downloadImportReport(reporte.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url; a.download = reporte.filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setAlert({ type: 'danger', message: 'No se pudo descargar el reporte (puede que ya haya expirado)' });
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-file-excel ${styles.iconoCabecera}`} />Reportes de importación</h1>
          <p>Descarga el resultado de cada carga masiva de propietarios</p>
        </div>
        <div className={styles.acciones}>
          {(isSuperAdmin || (isAdminMultiProp && properties.length > 1)) && (
            <div className={styles.filtro}>
              <i className={`fas fa-building ${styles.iconoFiltro}`} />
              <PropertyCombobox value={filterProp} onChange={cambiarPropiedad} placeholder="Todas las propiedades" />
              {filterProp && <button className="btn btn-sm btn-secondary btn-icon" onClick={() => cambiarPropiedad('')} title="Limpiar">
                <i className="fas fa-times" /></button>}
            </div>
          )}
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} ${styles.aviso}`} onClick={() => setAlert(null)}>
          {alert.message}
        </div>
      )}

      <div className="card">
        <div className={`card-body ${styles.cuerpo}`}>
          <div className={styles.info}>
            <i className={`fas fa-circle-info ${styles.iconoInfo}`} />
            Los reportes de importación se conservan {retencion} días desde que se generaron y luego se eliminan automáticamente.
          </div>

          {loading ? <div className="loading"><div className="spinner" /></div> :
            !visibleReportes.length ? (
              <div className="empty-state">
                <i className="fas fa-file-excel" />
                <p>{filterProp ? `No hay reportes de importación para esta propiedad en los últimos ${retencion} días.` : `No hay reportes de importación en los últimos ${retencion} días.`}</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Fecha</th><th>Propiedad</th><th>Usuario</th><th>Creados</th><th>Actualizados</th><th>Errores</th><th>Pendientes</th><th></th></tr>
                  </thead>
                  <tbody>
                    {pageRows.map(r => (
                      <tr key={r.id}>
                        <td>{fmtFecha(r.createdAt)}</td>
                        <td>{r.property?.name || '—'}</td>
                        <td>{r.user ? `${r.user.name} ${r.user.last_name || ''}`.trim() : '—'}</td>
                        <td><span className={styles.cifraOk}>{r.created_count}</span></td>
                        <td><span className={styles.cifraAviso}>{r.updated_count}</span></td>
                        <td><span className={styles.cifraError}>{r.error_count}</span></td>
                        <td><span className={styles.cifraPendiente}>{r.pendientes_count}</span></td>
                        <td>
                          <button className="btn btn-sm btn-secondary" onClick={() => handleDownload(r)} title="Descargar reporte">
                            <i className="fas fa-file-download" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>
      {!loading && <Paginacion pagina={safePage} total={visibleReportes.length} porPagina={PAGE_SIZE} onCambiar={irAPagina} />}
    </div>
  );
}
